import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { useEventLogging } from '@innovamat/event-logging';
import { Typography } from '@innovamat/glimmer-components';
import type { GlowApiError } from '@innovamat/glow-api-client';

import { useSchoolCapabilities } from '../../hooks/use-school-capabilities';
import {
  AddClassroomDropdown,
  ClassroomDropdownOptions,
  type AddClassroomDropdownOption,
  type AssignEventType,
} from '../add-classroom-dropdown';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

type Props = {
  isDisabled: boolean;
  onJoinClassroom?: () => void;
  onJoinJourney?: () => void;
  onError: (error: GlowApiError) => void;
};

function MyClassesHeader({
  isDisabled,
  onJoinClassroom,
  onJoinJourney,
  onError,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const { setEventData } = useEventLogging();

  const {
    classrooms: {
      canCreateClassrooms,
      canCreateJourneys,
      canOnlyAssignToClassrooms,
    },
  } = useSchoolCapabilities();

  const handleClassroomDropdownEvent = (type: AssignEventType): void => {
    setEventData('launcher_teacher_assign_to_class', { assign_type: type });
  };

  const createOptions = (): AddClassroomDropdownOption[] => {
    if (canCreateJourneys) {
      return [
        ClassroomDropdownOptions.ADD_JOURNEY,
        ClassroomDropdownOptions.JOIN_JOURNEY,
      ];
    }

    if (canOnlyAssignToClassrooms) {
      return [ClassroomDropdownOptions.JOIN];
    }

    if (canCreateClassrooms) {
      return [
        ClassroomDropdownOptions.ADD,
        ClassroomDropdownOptions.IMPORT,
        ClassroomDropdownOptions.JOIN,
      ];
    }

    return [];
  };

  return (
    <Header>
      <Typography.H3>
        {canCreateJourneys
          ? t('my-classes.header.title-journey')
          : t('Mis clases')}
      </Typography.H3>
      <AddClassroomDropdown
        disabled={isDisabled}
        onJoinClassroom={onJoinClassroom}
        onJoinJourney={onJoinJourney}
        options={createOptions()}
        onError={onError}
        setEvent={handleClassroomDropdownEvent}
      />
    </Header>
  );
}

export { MyClassesHeader };
